import { MaterialIcons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Dimensions, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Colors from '../../constants/Colors';
import Languages from '../../constants/Languages';
import Layout from '../../constants/Layout';
import MaterialSwitch from '../../utils/switch';
import NavBar from '../NavBar';
import DeleteAccount from './DeleteAccount';
import PushNotificationRegistration from './PushNotificationRegistration';
import { releaseId } from './releaseId';
import { useExpoModules } from '../../providers/ExpoModulesProvider';

function UserSettings({ translation, actions, profile, navigation, registration }) {
  const { Updates, Constants } = useExpoModules();

  const [state, setState] = useState({
    errors: {},
    user: Object.assign({}, profile),
    showChangeLanguage: false,
    showSaveChanges: false,
    pushNotificationRegistration: false,
    switchesLoaded: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, switchesLoaded: true, showSaveChanges: false }));
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const { user: newUser } = registration;
    if (state.user.id !== newUser.id) {
      setState((prev) => ({ ...prev, user: Object.assign({}, newUser) }));
    }
  }, [registration, state.user.id]);

  const somethingChanged = (user) => {
    const updates = {};
    Object.keys(profile).forEach((key) => {
      if (profile[key] !== user[key]) updates[key] = user[key];
    });
    return { changes: updates, changed: !!Object.keys(updates).length };
  };

  const onChangeLanguage = (value) => {
    const user = { ...state.user, language: value };
    const updates = somethingChanged(user);
    setState((prev) => ({
      ...prev,
      user,
      showChangeLanguage: false,
      showSaveChanges: updates.changed,
    }));
  };

  const setChanges = (field, value) => {
    const user = { ...state.user };
    if (field === 'email_unsubscribed' || field === 'receive_notifications') {
      user[field] = !!value;
    } else {
      user[field] = value;
    }
    const updates = somethingChanged(user);
    setState((prev) => ({ ...prev, user, showSaveChanges: updates.changed }));
  };

  const setNotifications = () => {
    setChanges('receive_notifications', 1);
    setState((prev) => ({ ...prev, pushNotificationRegistration: true }));
  };

  const removeNotifications = () => {
    setChanges('receive_notifications', 0);
  };

  const subscribeToEmail = () => {
    setChanges('email_unsubscribed', 0);
  };

  const unSubscribeToEmail = () => {
    setChanges('email_unsubscribed', 1);
  };

  const cancelShowSaveChanges = () => {
    setState((prev) => ({ ...prev, showSaveChanges: false }));
  };

  const confirmSaveChanges = () => {
    setState((prev) => ({ ...prev, showSaveChanges: false }));
    const updates = somethingChanged(state.user);
    if (updates.changed) {
      actions.updateUser(updates.changes);
    }
  };

  const capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const deleteAccount = () => {
    actions.deleteAccount(() => {
      actions.logout(() => {
        navigation.navigate('Language');
      });
    });
  };

  const openDeleteAccount = () => {
    actions.openDeleteAccount();
  };

  const closeDeleteAccount = () => {
    actions.closeDeleteAccount();
  };

  const { showSaveChanges, switchesLoaded, user, pushNotificationRegistration } = state;
  const { showDeleteAccount } = registration;

  const labels = {
    app_release_id: translation.app_release_id,
    receive_notifications: translation.fragment_profile_switch_receivenotifications,
    language: translation.fragment_profile_textview_language,
    notifications: capitalize(translation.fragment_profile_textview_notifications),
    settings: capitalize(translation.settings),
    save_changes: translation.fragment_profile_button_savechanges,
    select_language: translation.fragment_registration_language_textview_title,
    email_subscribe: translation.email_subscribe,
    delete_account: translation.delete_account,
  };

  const languages = Languages.languages;
  let userLanguage;
  languages.forEach((lang) => {
    if (lang.full === user.language) userLanguage = lang.full;
  });

  let pushComponent;
  if (pushNotificationRegistration) {
    pushComponent = <PushNotificationRegistration user={user} actions={actions} translation={translation} />;
  }

  const dropdownValues = [];
  Languages.languages.forEach((lang, index) => {
    dropdownValues.push(<Picker.Item key={`Language-${index}`} label={lang.label} value={lang.full} />);
  });

  let emailSubscribed = true;
  let showEmailSubscribed = true;
  if (Boolean(user.email_unsubscribed)) emailSubscribed = false;
  if (!user.email || user.email === '') showEmailSubscribed = false;

  let deleteAccountModal = null;
  if (showDeleteAccount) {
    deleteAccountModal = (
      <DeleteAccount onOk={deleteAccount} onCancel={closeDeleteAccount} translation={translation} />
    );
  }

  return (
    <View style={styles.container}>
      <NavBar title={labels.settings} onPressBack={() => navigation.navigate('landing')} />

      {showSaveChanges && switchesLoaded ? (
        <View style={styles.saveChangesContainer}>
          <TouchableOpacity onPress={cancelShowSaveChanges} style={styles.cancelSaveChanges}>
            <MaterialIcons name={'close'} size={24} color={Colors.white} />
          </TouchableOpacity>
          <TouchableOpacity onPress={confirmSaveChanges} style={styles.cancelSaveChanges}>
            <Text style={styles.saveChangesText}>{labels.save_changes}</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <View style={styles.container}>
        <View style={styles.mainContent}>
          <View style={styles.subheader}>
            <Text style={styles.subheaderText}>{labels.language}</Text>
          </View>
          <View style={styles.infoCard}>
            <View style={styles.row}>
              <Picker
                mode="dropdown"
                selectedValue={userLanguage}
                placeholder={labels.language}
                onValueChange={onChangeLanguage}
                style={styles.dropdown}
              >
                {dropdownValues}
              </Picker>
            </View>
          </View>

          <View style={styles.subheader}>
            <Text style={styles.subheaderText}>{labels.notifications}</Text>
          </View>
          <View style={styles.infoCard}>
            <View style={styles.viewWithRow}>
              <Text style={styles.detailText}>{labels.receive_notifications}</Text>
              <View>
                <MaterialSwitch
                  onActivate={setNotifications}
                  onDeactivate={removeNotifications}
                  active={Boolean(user.receive_notifications)}
                />
              </View>
            </View>

            {showEmailSubscribed ? (
              <View style={styles.viewWithRow}>
                <Text style={styles.detailText}>{labels.email_subscribe}</Text>
                <View>
                  <MaterialSwitch
                    onActivate={subscribeToEmail}
                    onDeactivate={unSubscribeToEmail}
                    active={emailSubscribed}
                  />
                </View>
              </View>
            ) : null}
          </View>

          <View style={styles.deleteAccountContainer}>
            <TouchableOpacity onPress={openDeleteAccount}>
              <Text style={styles.deleteAccountText}>{labels.delete_account}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.releaseContainer}>
            <Text style={styles.releaseText}>{labels.app_release_id}</Text>
            <Text style={styles.releaseText}>{releaseId(Updates, Constants)}</Text>
          </View>
        </View>

        {pushComponent}
        {deleteAccountModal}
      </View>
    </View>
  );
}

UserSettings.propTypes = {
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  profile: PropTypes.objectOf(PropTypes.any).isRequired,
  navigation: PropTypes.objectOf(PropTypes.any).isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    flex: 1,
    alignItems: 'center',
  },
  mainContent: {
    flex: 1,
    maxWidth: 720,
    width: '100%',
  },
  infoCard: {
    padding: 16,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
  },
  subheader: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 12,
    backgroundColor: Colors.lightBackground,
  },
  subheaderText: {
    fontSize: 14,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  label: {
    color: Colors.grayText,
    fontSize: 12,
  },
  dropdownIcon: {
    color: Colors.lightGray,
  },
  dropdownPlaceholder: {
    color: Colors.grayText,
    fontFamily: 'roboto-regular',
  },
  dropdown: {
    width: Dimensions.get('window').width - 32,
    maxWidth: 688,
    borderColor: Colors.white,
    backgroundColor: Colors.white,
  },
  cardView: {
    marginHorizontal: 0,
    marginVertical: 0.5,
    backgroundColor: Colors.lightBackground,
    paddingVertical: 20,
    elevation: 2,
  },
  descriptionText: {
    paddingTop: 36,
    paddingBottom: 16,
    paddingLeft: 8,
    fontSize: 20,
    color: '#808080',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 20,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  textStyle: {
    fontSize: 20,
    color: Colors.veryDarkGray,
  },
  verifyText: {
    fontSize: 18,
    color: Colors.profileVerified,
  },
  desSec: {
    padding: 10,
  },
  viewWithRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  viewWithXtaPadding: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 36,
  },
  dashedUnderline: {
    borderWidth: 1,
    borderColor: Colors.grayBorder,
    borderStyle: 'dashed',
    borderRadius: 1,
    width: '80%',
    marginBottom: 16,
  },
  detailText: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.veryDarkGray,
    maxWidth: Layout.fullWidth - 100,
  },
  activeText: {
    color: Colors.veryDarkGray,
  },
  labelTextStyle: {
    position: 'absolute',
    left: '100%',
  },
  halfWidth: {
    width: '50%',
  },
  saveChangesContainer: {
    position: 'absolute',
    backgroundColor: Colors.mediumBlue,
    height: 56,
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelSaveChanges: {
    paddingLeft: 16,
  },
  saveChangesText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginRight: 16,
  },
  deleteAccountContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  deleteAccountText: {
    color: Colors.brightRed,
    fontSize: 16,
  },
  releaseContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 20,
  },
  releaseText: {
    color: Colors.lightGray,
  },
});

export default UserSettings;
